import {memo, useCallback, useMemo} from 'react';

import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {Box, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
  DatePickerInput,
} from '@/app/shared-components';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import {IRecordGroupExpenseRequest} from '@/dataSource/src/api/groups/entities';

import {IGroupMember} from '../../models/interfaces';
import {GroupSelectPayerModal} from '../GroupSelectPayerModal';

import {MAX_DATE, MIN_DATE, MO_ICON_SRC} from './constants';
import {useGroupAddExpenseForm} from './useGroupAddExpenseForm';

export {EAddExpenseSplitMode} from './constants';

interface AddExpenseFormProps {
  groupName: string;
  inviterName: string;
  members: IGroupMember[];
  currentUserId?: string;
  onSubmit: (payload: IRecordGroupExpenseRequest) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Member row (Equal split list)
// ---------------------------------------------------------------------------

type MemberRowProps = {
  member: IGroupMember;
  payer?: IGroupMember;
  selected: boolean;
  shareLabel: string;
  onToggle: (memberId: string) => void;
  t: TFunction;
};

const MemberRow = memo(function MemberRow({
  member,
  payer,
  selected,
  shareLabel,
  onToggle,
  t,
}: MemberRowProps) {
  const handleClick = useCallback(
    () => onToggle(member.id),
    [member.id, onToggle],
  );

  const subtitle = useMemo(() => {
    if (!payer || !selected) {
      return undefined;
    }

    // Current user owes another participant
    if (member.isYou && !payer.isYou) {
      return t('groups.add_expense_form.you_owe', {
        name: payer.name,
      });
    }

    // Another participant owes current user
    if (!member.isYou && payer.isYou) {
      return t('groups.add_expense_form.owes_you');
    }

    return undefined;
  }, [member.isYou, payer, selected, t]);

  const memberName = useMemo(() => {
    if (member.isYou) {
      return `${member.name} (${t('groups.add_expense_form.you')})`;
    }
    return member.name;
  }, [member.isYou, member.name, t]);

  return (
    <Box
      id={`GroupAddExpense_memberRow_${member.id}`}
      className="group-add-expense-form-member-row"
      onClick={handleClick}>
      <Box
        className={`group-add-expense-form-checkbox${selected ? ' checked' : ''}`}>
        {selected && (
          <CheckIcon className="group-add-expense-form-checkbox-icon" />
        )}
      </Box>

      <Box className="group-add-expense-form-member-info">
        <Typography className="group-add-expense-form-member-name">
          {memberName}
        </Typography>

        {!!subtitle && (
          <Typography className="group-add-expense-form-member-badge">
            {subtitle}
          </Typography>
        )}
      </Box>

      <Typography className="group-add-expense-form-member-share">
        {shareLabel}
      </Typography>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Main form (presentational — state lives in `useGroupAddExpenseForm`)
// ---------------------------------------------------------------------------

export function GroupAddExpenseForm({
  groupName,
  inviterName,
  members,
  currentUserId,
  onSubmit,
}: Readonly<AddExpenseFormProps>) {
  const {
    title,
    amountLabel,
    when,
    selectedMemberIds,
    selectedPayerId,
    selectedPayerName,
    selectedPayer,
    payerModalOpen,
    canSubmit,
    isEqualMode,
    isCustomMode,
    handleTitleChange,
    handleAmountChange,
    handleWhenChange,
    handleToggleMember,
    handleSetSplitEqual,
    handleSetSplitCustom,
    handleOpenPayerModal,
    handleClosePayerModal,
    handleConfirmPayer,
    handleSubmit,
    handleOnClickSplitWithMo,
    getMemberShareLabel,
  } = useGroupAddExpenseForm({
    groupName,
    inviterName,
    members,
    currentUserId,
    onSubmit,
  });

  const {t} = useTranslation();

  return (
    <Box className="group-add-expense-form">
      <Box className="add-group-expense-form-title-label">
        <InputTextField
          id="GroupAddExpense_titleInput"
          label={t('groups.add_expense_form.title_label')}
          value={title}
          onChange={handleTitleChange}
        />
      </Box>

      <Box className="group-add-expense-form-row">
        <Box className="add-group-expense-form-amount-label">
          <InputTextField
            id="GroupAddExpense_amountInput"
            label={t('groups.add_expense_form.amount_label')}
            value={amountLabel}
            onChange={handleAmountChange}
          />
        </Box>

        <Box className="group-add-expense-form-input">
          <DatePickerInput
            id="GroupAddExpense_dateInput"
            label={t('groups.add_expense_form.when_label')}
            value={when}
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={handleWhenChange}
            format="MMM d"
          />
        </Box>
      </Box>

      <Box
        id="GroupAddExpense_payerTrigger"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={payerModalOpen}
        onClick={handleOpenPayerModal}
        className={`group-add-expense-form-paid-by${payerModalOpen ? ' open' : ''}`}>
        <Box className="group-add-expense-form-paid-by-text">
          <Typography className="group-add-expense-form-paid-by-label">
            {t('groups.add_expense_form.paid_by_label')}
          </Typography>

          {!!selectedPayerName && (
            <Typography className="group-add-expense-form-paid-by-value">
              {selectedPayerName}
            </Typography>
          )}
        </Box>

        <KeyboardArrowDownIcon
          className={`group-add-expense-form-paid-by-icon${payerModalOpen ? ' open' : ''}`}
        />
      </Box>

      <Box className="group-add-expense-form-toggle">
        <Box
          id="GroupAddExpense_equalSplitChip"
          role="button"
          tabIndex={0}
          onClick={handleSetSplitEqual}
          className={`group-add-expense-form-toggle-chip${isEqualMode ? ' active' : ''}`}>
          {t('groups.add_expense_form.split_equal')}
        </Box>

        <Box
          id="GroupAddExpense_customSplitChip"
          role="button"
          tabIndex={0}
          onClick={handleSetSplitCustom}
          className={`group-add-expense-form-toggle-chip${isCustomMode ? ' active' : ''}`}>
          {t('groups.add_expense_form.split_custom')}
        </Box>
      </Box>

      {isCustomMode ? (
        <Box className="group-add-expense-form-custom-card">
          <Box className="group-add-expense-form-custom-icon-text">
            <Box className="group-add-expense-form-custom-icon">
              <img
                src={MO_ICON_SRC}
                alt=""
                aria-hidden="true"
              />
            </Box>

            <Typography className="group-add-expense-form-custom-text">
              {t('groups.add_expense_form.custom_description')}
            </Typography>
          </Box>

          <Box className="group-add-expense-form-custom-button">
            <AppButton
              id="GroupAddExpense_splitWithMoButton"
              onClick={handleOnClickSplitWithMo}
              label={t('groups.add_expense_form.split_with_mo')}
              isAuto
              variant={AppButtonVariant.onHelp}
            />
          </Box>
        </Box>
      ) : (
        <Box className="group-add-expense-form-members-card">
          {members.map(member => (
            <MemberRow
              key={member.id}
              member={member}
              payer={selectedPayer}
              selected={selectedMemberIds.includes(member.id)}
              shareLabel={getMemberShareLabel(member.id)}
              onToggle={handleToggleMember}
              t={t}
            />
          ))}
        </Box>
      )}

      <ButtonContainer sticky>
        <AppButton
          id="GroupAddExpense_submitButton"
          variant={AppButtonVariant.onPrimary}
          disabled={!canSubmit}
          label={t('groups.add_expense_form.submit')}
          onClick={handleSubmit}
        />
      </ButtonContainer>

      <GroupSelectPayerModal
        open={payerModalOpen}
        onClose={handleClosePayerModal}
        onConfirm={handleConfirmPayer}
        members={members}
        selectedPayerId={selectedPayerId}
        currentUserId={currentUserId}
        expenseTitle={title}
      />
    </Box>
  );
}
